/**
 * VAT arithmetic in öre and the momsomföring draft. Pure.
 */
import {
  ROUNDING_ACCOUNT,
  VAT_RECEIVABLE_ACCOUNT,
  VAT_SETTLEMENT_ACCOUNT,
  isVatClearingAccount,
  truncateToKronor,
} from '../bas/momskoder';
import type { Ore } from '../money';
import type { IsoDate, VatRate, VerifikationRow } from '../types';
import type { VerifikationDraft } from '../verifikationer/rules';

/** Round half away from zero, on integers only. */
function roundDiv(numerator: number, denominator: number): number {
  const sign = numerator < 0 ? -1 : 1;
  const abs = Math.abs(numerator);
  return sign * Math.floor((2 * abs + denominator) / (2 * denominator));
}

/** VAT contained in a gross amount: gross × rate / (100 + rate), rounded to öre. */
export function vatFromGross(grossOre: Ore, rate: VatRate): Ore {
  if (!Number.isInteger(grossOre)) throw new RangeError('vatFromGross needs an integer öre amount');
  if (rate === 0) return 0;
  return roundDiv(grossOre * rate, 100 + rate);
}

/** VAT on a net amount: net × rate / 100, rounded to öre. */
export function vatOnNet(netOre: Ore, rate: VatRate): Ore {
  if (!Number.isInteger(netOre)) throw new RangeError('vatOnNet needs an integer öre amount');
  if (rate === 0) return 0;
  return roundDiv(netOre * rate, 100);
}

/**
 * Split a gross amount into net and VAT. A printed VAT amount (from the
 * receipt) wins over the computed one as long as it fits inside the gross.
 */
export function splitGross(grossOre: Ore, rate: VatRate, printedVatOre?: Ore): { netOre: Ore; vatOre: Ore } {
  const vatOre =
    printedVatOre !== undefined && Number.isInteger(printedVatOre) && printedVatOre >= 0 && printedVatOre <= grossOre
      ? printedVatOre
      : vatFromGross(grossOre, rate);
  return { netOre: grossOre - vatOre, vatOre };
}

export interface VatSettlement {
  draft: VerifikationDraft;
  /** Positive = moms att betala (credited to 2650); negative = moms att få tillbaka (debited to 1650); whole kronor. */
  payableOre: Ore;
  /** Öre difference booked on 3740. */
  roundingOre: Ore;
}

/**
 * Momsomföring for a period: clears every 2610–2649 account against 2650
 * (payable) or 1650 (refund), booking the öre remainder on 3740 so the
 * declared amount is whole kronor.
 *
 * `periodBalances` holds the *period movements* per account with the ledger
 * sign convention debit +, credit −. Accounts outside 2610–2649 are ignored.
 * An empty result (no VAT movements) returns a draft without rows; callers
 * must not book that.
 */
export function vatSettlementDraft(periodBalances: ReadonlyMap<number, Ore>, periodEnd: IsoDate): VatSettlement {
  const rows: VerifikationRow[] = [];
  let net = 0; // debits − credits so far
  const numbers = [...periodBalances.keys()].filter(isVatClearingAccount).sort((a, b) => a - b);
  for (const accountNumber of numbers) {
    const balance = periodBalances.get(accountNumber) ?? 0;
    if (balance === 0) continue;
    // Zero the account: a credit balance (negative) needs a debit and vice versa.
    if (balance < 0) {
      rows.push({ accountNumber, debit: -balance, credit: 0 });
      net += -balance;
    } else {
      rows.push({ accountNumber, debit: 0, credit: balance });
      net -= balance;
    }
  }
  if (rows.length === 0) {
    return { draft: { date: periodEnd, description: `Momsredovisning t.o.m. ${periodEnd}`, rows: [] }, payableOre: 0, roundingOre: 0 };
  }
  const payableOre = truncateToKronor(net);
  const roundingOre = net - payableOre;
  if (payableOre > 0) rows.push({ accountNumber: VAT_SETTLEMENT_ACCOUNT, debit: 0, credit: payableOre });
  else if (payableOre < 0) rows.push({ accountNumber: VAT_RECEIVABLE_ACCOUNT, debit: -payableOre, credit: 0 });
  if (roundingOre > 0) rows.push({ accountNumber: ROUNDING_ACCOUNT, debit: 0, credit: roundingOre });
  else if (roundingOre < 0) rows.push({ accountNumber: ROUNDING_ACCOUNT, debit: -roundingOre, credit: 0 });
  return {
    draft: { date: periodEnd, description: `Momsredovisning t.o.m. ${periodEnd}`, rows },
    payableOre,
    roundingOre,
  };
}
